import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useLayoutEffect, useRef, useState } from "react";
import { units } from "@m/alchemy-theme";
import ResizeObserver from "resize-observer-polyfill";
import { combineRefs } from "../../combine-refs";
import { CharacterCount } from "../character-count/CharacterCount";
import * as Styled from "./TextArea.styled";
export const TextArea = forwardRef((props, ref) => {
    /* istanbul ignore next */
    const { disabled, isFullWidth, hasStatus, palette = "interactiveConstructive", minHeight = "53px" /* existing text area min-height not set in css + 3px for Firefox */, maxHeight = "60vh", readOnly, resize, id, "aria-describedby": ariaDescribedby = "", stringMap, maxLength, ...otherProps } = props;
    const stripUnits = (numberString) => parseFloat(numberString);
    const textAreaRef = useRef(null);
    const hasCharacterCount = Boolean(maxLength && !readOnly && !disabled);
    const calcMinHeight = hasCharacterCount ? `${stripUnits(minHeight) + 24}px` : minHeight;
    const [textAreaHeight, setTextAreaHeight] = useState(calcMinHeight);
    const characterCountId = hasCharacterCount ? id + "-cc" : "";
    const [hasFocus, setHasFocus] = useState(false);
    const handleKeyboardResize = () => {
        if (resize === "none" || resize === "horizontal") {
            return false;
        }
        if (textAreaRef.current && textAreaRef.current.scrollHeight > stripUnits(textAreaHeight)) {
            setTextAreaHeight(`${textAreaRef.current.scrollHeight}px`);
        }
        return true;
    };
    const handleMouseResize = useCallback(() => {
        if (textAreaRef.current) {
            const { height } = textAreaRef.current.getBoundingClientRect();
            setTextAreaHeight(`${height}px`);
        }
        return true;
    }, []);
    useLayoutEffect(() => {
        if (!textAreaRef?.current) {
            return;
        }
        const widthTarget = textAreaRef.current;
        const resizeObserver = new ResizeObserver(handleMouseResize);
        resizeObserver.observe(widthTarget);
        handleMouseResize();
        return () => {
            resizeObserver.disconnect();
        };
    }, [handleMouseResize]);
    return (_jsxs(Styled.TextAreaWrapper, { isFullWidth: Boolean(isFullWidth), style: {
            maxHeight,
            height: stripUnits(textAreaHeight) > stripUnits(calcMinHeight) ? textAreaHeight : calcMinHeight,
            minHeight: calcMinHeight
        }, children: [resize !== "none" && readOnly === undefined && _jsx(Styled.TextAreaWrapperResizer, { size: 10, "aria-hidden": "true" }), _jsx(Styled.TextArea, { ref: combineRefs(textAreaRef, ref), "data-testid": "TextArea", isFullWidth: isFullWidth, hasStatus: hasStatus, palette: palette, maxHeight: maxHeight, onKeyDown: () => handleKeyboardResize(), readOnly: readOnly, resize: resize, style: {
                    maxHeight,
                    height: stripUnits(textAreaHeight) > stripUnits(calcMinHeight) ? textAreaHeight : calcMinHeight,
                    minHeight: calcMinHeight
                }, showCharacterCount: hasCharacterCount, disabled: disabled, maxLength: maxLength, id: id, "aria-describedby": ariaDescribedby || characterCountId ? [characterCountId, ariaDescribedby].join(" ") : undefined, ...otherProps, onFocus: (e) => {
                    otherProps.onFocus?.(e);
                    setHasFocus(true);
                }, onBlur: (e) => {
                    otherProps.onBlur?.(e);
                    if (!e.currentTarget.contains(e.relatedTarget)) {
                        setHasFocus(false);
                    }
                } }), hasCharacterCount && (_jsx(CharacterCount, { id: characterCountId || undefined, isLive: hasFocus, elementRef: textAreaRef, maxLength: maxLength, stringMap: stringMap, isRelativeToParent: true, offsetRight: units(1) }))] }));
});
TextArea.defaultProps = {
    /** @deprecated */
    backgroundVariant: "container",
    isFullWidth: false,
    resize: "vertical",
    minHeight: "53px" /* existing text area min-height */,
    maxHeight: "60vh"
};
TextArea.displayName = "TextArea";
